import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ExtendedPropertyDetail, GridReloadRequest, PropertyPageView } from '../interface/property.interface';

@Injectable({
  providedIn: 'root',
})
export class PropertyState {
  /** State where a property's page view layout config is stored */
  private _propertyPageLayoutSub$: BehaviorSubject<PropertyPageView | null> =
    new BehaviorSubject<PropertyPageView | null>(null);
  propertyPageLayout$: Observable<PropertyPageView | null> = this._propertyPageLayoutSub$.asObservable();

  get propertyPageLayout(): PropertyPageView | null {
    return this._propertyPageLayoutSub$.value;
  }

  set propertyPageLayout(propertyData: PropertyPageView | null) {
    this._propertyPageLayoutSub$.next(propertyData);
  }

  /** State where subject page's view layout config is stored */
  private _subjectPageLayoutSub$: BehaviorSubject<PropertyPageView | null> =
    new BehaviorSubject<PropertyPageView | null>(null);
  subjectPageLayout$: Observable<PropertyPageView | null> = this._subjectPageLayoutSub$.asObservable();

  get subjectPageLayout(): PropertyPageView | null {
    return this._subjectPageLayoutSub$.value;
  }

  set subjectPageLayout(layoutData: PropertyPageView | null) {
    this._subjectPageLayoutSub$.next(layoutData);
  }

  /** State where a comparable's page view layout config is stored */
  private _comparablePageLayoutSub$: BehaviorSubject<PropertyPageView | null> =
    new BehaviorSubject<PropertyPageView | null>(null);
  comparablePageLayout$: Observable<PropertyPageView | null> = this._comparablePageLayoutSub$.asObservable();

  get comparablePageLayout(): PropertyPageView | null {
    return this._comparablePageLayoutSub$.value;
  }

  set comparablePageLayout(propertyData: PropertyPageView | null) {
    this._comparablePageLayoutSub$.next(propertyData);
  }

  /** State where a project's page view layout config is stored */
  private _projectPageLayoutSub$ = new BehaviorSubject<PropertyPageView | null>(null);
  projectPageLayout$: Observable<PropertyPageView | null> = this._projectPageLayoutSub$.asObservable();

  get projectPageLayout(): PropertyPageView | null {
    return this._projectPageLayoutSub$.value;
  }

  set projectPageLayout(layoutData: PropertyPageView | null) {
    this._projectPageLayoutSub$.next(layoutData);
  }

  /** State where a subject image view layout config is stored */
  private _subjectImageLayoutSub$ = new BehaviorSubject<PropertyPageView | null>(null);
  subjectImageLayout$: Observable<PropertyPageView | null> = this._subjectImageLayoutSub$.asObservable();

  get subjectImageLayout(): PropertyPageView | null {
    return this._subjectImageLayoutSub$.value;
  }

  set subjectImageLayout(layoutData: PropertyPageView | null) {
    this._subjectImageLayoutSub$.next(layoutData);
  }

  /** State where a comparable image view layout config is stored */
  private _comparableImageLayoutSub$ = new BehaviorSubject<PropertyPageView | null>(null);
  comparableImageLayout$: Observable<PropertyPageView | null> = this._comparableImageLayoutSub$.asObservable();

  get comparableImageLayout(): PropertyPageView | null {
    return this._comparableImageLayoutSub$.value;
  }

  set comparableImageLayout(layoutData: PropertyPageView | null) {
    this._comparableImageLayoutSub$.next(layoutData);
  }

  /** State where subject property's data is stored */
  private _subjectPropertyDataSub$ = new BehaviorSubject<ExtendedPropertyDetail | null>(null);
  subjectPropertyData$: Observable<ExtendedPropertyDetail | null> = this._subjectPropertyDataSub$.asObservable();

  get subjectPropertyData(): ExtendedPropertyDetail | null {
    return this._subjectPropertyDataSub$.value;
  }

  set subjectPropertyData(propertyData: ExtendedPropertyDetail | null) {
    this._subjectPropertyDataSub$.next(propertyData);
  }

  /** State where an active property's data is stored */
  private _activePropertyDataSub$ = new BehaviorSubject<ExtendedPropertyDetail | null>(null);
  activePropertyData$: Observable<ExtendedPropertyDetail | null> = this._activePropertyDataSub$.asObservable();

  get activePropertyData(): ExtendedPropertyDetail | null {
    return this._activePropertyDataSub$.value;
  }

  set activePropertyData(propertyData: ExtendedPropertyDetail | null) {
    this._activePropertyDataSub$.next(propertyData);
  }

  /** State where an active property's active history data is stored */
  private _activePropertyHistoryDataSub$ = new BehaviorSubject<ExtendedPropertyDetail | null>(null);
  activePropertyHistoryData$: Observable<ExtendedPropertyDetail | null> =
    this._activePropertyHistoryDataSub$.asObservable();

  get activePropertyHistoryData(): ExtendedPropertyDetail | null {
    return this._activePropertyHistoryDataSub$.value;
  }

  set activePropertyHistoryData(propertyData: ExtendedPropertyDetail | null) {
    this._activePropertyHistoryDataSub$.next(propertyData);
  }

  /** State where active history listingId or not is stored */
  private _activePropertyHistoryListingIdSub$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(
    null
  );
  activePropertyHistoryListingId$: Observable<string | null> = this._activePropertyHistoryListingIdSub$.asObservable();

  get activePropertyHistoryListingId(): string | null {
    return this._activePropertyHistoryListingIdSub$.value;
  }

  set activePropertyHistoryListingId(value: string | null) {
    this._activePropertyHistoryListingIdSub$.next(value);
  }

  /** Emits the affected property when the comps grid must regenerate after units change or a sub type change is reverted. */
  private _gridReloadRequiredSub$ = new Subject<GridReloadRequest>();
  gridReloadRequired$: Observable<GridReloadRequest> = this._gridReloadRequiredSub$.asObservable();

  set gridReloadRequired(request: GridReloadRequest) {
    this._gridReloadRequiredSub$.next(request);
  }

  /**
   * Emits the property id whenever the user edits PropertySubType, even when they re-pick the value it
   * already had. A re-select writes an identical value, so the property state observables alone cannot
   * tell it apart from an edit to any other field - this is the only signal that can.
   */
  private _propertySubTypeEditedSub$ = new Subject<string>();
  propertySubTypeEdited$: Observable<string> = this._propertySubTypeEditedSub$.asObservable();

  set propertySubTypeEdited(propertyId: string) {
    this._propertySubTypeEditedSub$.next(propertyId);
  }

  /** State where a property condition layout config is stored */
  private _propertyConditionLayoutSub$ = new BehaviorSubject<PropertyPageView | null>(null);
  propertyConditionLayout$: Observable<PropertyPageView | null> = this._propertyConditionLayoutSub$.asObservable();

  get propertyConditionLayout(): PropertyPageView | null {
    return this._propertyConditionLayoutSub$.value;
  }

  set propertyConditionLayout(layoutData: PropertyPageView | null) {
    this._propertyConditionLayoutSub$.next(layoutData);
  }
}
